import { mapPathControls, pathData } from '../domain/pathEditing';
import { calloutOutline, calloutTextLayout } from '../rendering/calloutGeometry';
import { canvasTextMeasure } from './PaperTextAnnotation';
import { Fragment, type ReactElement } from 'react';
import type { PaperSize } from '../domain/geometry';
import { pointsToUnits } from '../domain/page';
import { normalizedToPixel } from '../domain/bonds';
import type { ViewportTransform } from '../domain/viewport';
import { isBackgroundShape, type RenderableAnnotation } from '../domain/annotationRendering';
import {
  ANNOTATION_DASH,
  starPoints,
  SHADOW_COLOR,
  SHADOW_OFFSET_PT,
  SHADOW_OPACITY
} from '../rendering/annotationGeometry';

const pointsAttr = (points: readonly { readonly x: number; readonly y: number }[]) =>
  points.map((point) => `${point.x},${point.y}`).join(' ');

export function PaperShapeAnnotation({
  annotation,
  paperSize,
  viewport
}: {
  readonly annotation: Extract<RenderableAnnotation, { kind: 'shape' }>;
  readonly paperSize: PaperSize;
  readonly viewport: ViewportTransform;
}) {
  const stroke = annotation.outline === false ? 'none' : (annotation.color ?? 'currentColor');
  const dash = ANNOTATION_DASH[annotation.lineStyle ?? 'solid'];
  const strokeWidth =
    annotation.lineWidth !== undefined ? pointsToUnits(annotation.lineWidth) * viewport.zoom : undefined;
  const ownWidth = strokeWidth !== undefined ? { strokeWidth } : undefined;
  const pixels = annotation.points.map((point) => normalizedToPixel(point, paperSize));
  // #866 -- the shadow is a copy drawn first, offset down-right in grey (crisp, the same
  // in every export). It carries no annotation id, so selection and hit tests skip it.
  const shadowOffset = pointsToUnits(SHADOW_OFFSET_PT);
  // On screen the colour and strength come from the theme (--annotation-shadow-*, with the
  // export's black at 20% as the fallback) so a dark sheet still shows it.
  const shadowOpacity = `calc(var(--annotation-shadow-opacity, ${SHADOW_OPACITY}) * ${annotation.opacity ?? 1})`;
  const shadowProps = {
    className: 'annotation-shadow',
    stroke: annotation.outline === false ? 'none' : SHADOW_COLOR,
    pointerEvents: 'none' as const
  };
  const withShadow = (draw: (shadow: boolean) => ReactElement | null) =>
    annotation.shadow ? (
      <Fragment key={annotation.id}>
        {draw(true)}
        {draw(false)}
      </Fragment>
    ) : (
      draw(false)
    );
  if (annotation.callout && pixels[0] && pixels[1]) {
    const bubble = annotation.callout;
    const tip = normalizedToPixel(bubble.tip, paperSize);
    const { layout } = calloutTextLayout(bubble.paragraphs, pixels[0], pixels[1], canvasTextMeasure, bubble.variant);
    const label = bubble.paragraphs.map((p) => p.runs.map((r) => r.text).join('')).join('\n');
    return (
      <g className="callout-bubble" data-callout-id={annotation.id} role="group" aria-label={label || 'Callout'}>
        {bubble.variant === 'leader' ? (
          <rect
            className="callout-body-hit"
            data-annotation-id={annotation.id}
            x={Math.min(pixels[0].x, pixels[1].x)}
            y={Math.min(pixels[0].y, pixels[1].y)}
            width={Math.abs(pixels[1].x - pixels[0].x)}
            height={Math.abs(pixels[1].y - pixels[0].y)}
            fill="transparent"
            stroke="none"
            pointerEvents="fill"
          />
        ) : null}
        <path
          data-annotation-id={annotation.id}
          d={calloutOutline(pixels[0], pixels[1], tip, bubble.variant)}
          fill={bubble.variant === 'leader' ? 'none' : (annotation.fill ?? 'var(--panel-bg, white)')}
          stroke={stroke}
          strokeDasharray={dash}
          vectorEffect="non-scaling-stroke"
          style={ownWidth}
          opacity={annotation.opacity}
        />
        <g className="callout-text" fill={annotation.color ?? 'var(--text)'} stroke="none" pointerEvents="none">
          {layout.lines.map((line, i) => (
            <text key={i} x={line.x} y={line.baseline} textAnchor={line.anchor} xmlSpace="preserve">
              {line.spans.map((span, j) => (
                <tspan
                  key={j}
                  dy={span.dy || undefined}
                  fontFamily={span.fontFamily}
                  fontSize={span.fontSizePx}
                  fontWeight={span.bold ? 700 : 400}
                  fontStyle={span.italic ? 'italic' : undefined}
                >
                  {span.text}
                </tspan>
              ))}
            </text>
          ))}
        </g>
      </g>
    );
  }
  if (annotation.shape === 'path') {
    return withShadow((shadow) => (
      <path
        key={shadow ? `${annotation.id}-shadow` : annotation.id}
        {...(shadow
          ? { ...shadowProps, transform: `translate(${shadowOffset} ${shadowOffset})` }
          : { 'data-annotation-id': annotation.id, stroke, opacity: annotation.opacity })}
        d={pathData({
          ...annotation,
          points: pixels,
          ...(annotation.pathControls
            ? { pathControls: mapPathControls(annotation.pathControls, (p) => normalizedToPixel(p, paperSize)) }
            : {})
        })}
        fill="none"
        strokeDasharray={dash}
        vectorEffect="non-scaling-stroke"
        style={shadow ? { ...ownWidth, opacity: shadowOpacity } : ownWidth}
      />
    ));
  }
  const [topLeft, bottomRight] = pixels;
  if (!topLeft || !bottomRight) return null;
  const minX = Math.min(topLeft.x, bottomRight.x);
  const minY = Math.min(topLeft.y, bottomRight.y);
  const maxX = Math.max(topLeft.x, bottomRight.x);
  const maxY = Math.max(topLeft.y, bottomRight.y);
  const rotate = `rotate(${annotation.rotation ?? 0} ${(minX + maxX) / 2} ${(minY + maxY) / 2})`;
  const filled = annotation.fill !== undefined && annotation.fill !== 'none';
  const sharedFor = (shadow: boolean) => ({
    ...(shadow
      ? {
          ...shadowProps,
          className: filled ? 'annotation-shadow filled' : 'annotation-shadow',
          fill: filled ? SHADOW_COLOR : 'none',
          transform: `translate(${shadowOffset} ${shadowOffset}) ${rotate}`
        }
      : {
          'data-annotation-id': annotation.id,
          transform: rotate,
          fill: annotation.fill ?? 'none',
          stroke,
          opacity: annotation.opacity
        }),
    strokeDasharray: dash,
    vectorEffect: 'non-scaling-stroke' as const,
    style: shadow ? { ...ownWidth, opacity: shadowOpacity } : ownWidth
  });
  const keyFor = (shadow: boolean) => (shadow ? `${annotation.id}-shadow` : annotation.id);
  if (annotation.shape === 'ellipse') {
    return withShadow((shadow) => (
      <ellipse
        key={keyFor(shadow)}
        {...sharedFor(shadow)}
        cx={(minX + maxX) / 2}
        cy={(minY + maxY) / 2}
        rx={(maxX - minX) / 2}
        ry={(maxY - minY) / 2}
      />
    ));
  }
  if (annotation.shape === 'rect') {
    // #866 -- rounded corners: the radius in points, never more than half the short side.
    const radius = Math.min(pointsToUnits(annotation.cornerRadius ?? 0), (maxX - minX) / 2, (maxY - minY) / 2);
    return withShadow((shadow) => (
      <rect
        key={keyFor(shadow)}
        {...sharedFor(shadow)}
        x={minX}
        y={minY}
        width={maxX - minX}
        height={maxY - minY}
        {...(radius > 0 ? { rx: radius, ry: radius } : {})}
      />
    ));
  }
  if (annotation.shape === 'triangle') {
    return withShadow((shadow) => (
      <polygon
        key={keyFor(shadow)}
        {...sharedFor(shadow)}
        points={`${(minX + maxX) / 2},${minY} ${minX},${maxY} ${maxX},${maxY}`}
      />
    ));
  }
  return withShadow((shadow) => (
    <polygon key={keyFor(shadow)} {...sharedFor(shadow)} points={pointsAttr(starPoints(minX, minY, maxX, maxY))} />
  ));
}

export function PaperBackgroundLayer({
  annotations,
  paperSize,
  viewport
}: {
  readonly annotations: readonly RenderableAnnotation[];
  readonly paperSize: PaperSize;
  readonly viewport: ViewportTransform;
}) {
  return (
    <svg
      className="paper-layer background-annotation-layer"
      width="100%"
      height="100%"
      viewBox={`${-viewport.panX / viewport.zoom} ${-viewport.panY / viewport.zoom} ${paperSize.width / viewport.zoom} ${paperSize.height / viewport.zoom}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <g className="annotation-layer">
        {annotations
          .filter(isBackgroundShape)
          .map((annotation) =>
            annotation.kind === 'shape' ? (
              <PaperShapeAnnotation
                key={annotation.id}
                annotation={annotation}
                paperSize={paperSize}
                viewport={viewport}
              />
            ) : null
          )}
      </g>
    </svg>
  );
}
